import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import giftService from '../services/giftService';
import { GiftItem } from '../types/gift';

interface Product {
  id?: string;
  name: string;
  price: number;
  wasPrice?: number;
  brand: string;
  gauge: string;
  imageURL: string;
  stock: number;
  description: string;
  category?: string;
  type?: string;
  material?: string;
}

interface CartItem extends Product {
  quantity: number;
  type?: 'product' | 'gift' | 'bundle';
}

export interface AddToCartOptions {
  /** Legacy: previously skipped add-to-cart toast; kept for call-site compatibility */
  silent?: boolean;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity?: number, options?: AddToCartOptions) => void;
  removeFromCart: (productId: string) => void;
  increaseQuantity: (productId: string) => void;
  decreaseQuantity: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  isSideCartOpen: boolean;
  setSideCartOpen: (open: boolean) => void;
  // Gift functionality
  showGiftPopup: boolean;
  setShowGiftPopup: (show: boolean) => void;
  selectedGifts: GiftItem[];
  addGiftsToCart: (gifts: GiftItem[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

// Utility function to remove undefined values from objects/arrays for Firebase
const removeUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)).filter(item => item !== null && item !== undefined);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value !== undefined) {
          cleaned[key] = removeUndefinedValues(value);
        }
      }
    }
    return cleaned;
  }
  
  return obj;
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const localData = localStorage.getItem('cart');
      return localData ? JSON.parse(localData) : [];
    } catch (error) {
      console.error("Could not parse cart data from localStorage", error);
      return [];
    }
  });

  const [isSideCartOpen, setSideCartOpen] = useState(false);
  
  // Gift system state
  const [showGiftPopup, setShowGiftPopup] = useState(false);
  const [selectedGifts, setSelectedGifts] = useState<GiftItem[]>([]);
  const hasTriggeredGifts = useRef(false); // Track if gifts have been triggered in this session

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    localStorage.setItem('cart', JSON.stringify(cartItems));

    // If user is logged in, sync with Firestore
    if (currentUser) {
      const cartDocRef = doc(db, 'carts', currentUser.uid);
      // Clean cart items to remove undefined values before saving to Firebase
      const cleanedCartItems = removeUndefinedValues(cartItems);
      setDoc(cartDocRef, { items: cleanedCartItems }).catch((error) => {
        console.error('Error saving cart to Firestore:', error);
      });
    }
  }, [cartItems, currentUser]);

  useEffect(() => {
    // When user logs in, load their cart from Firestore
    const loadCartFromFirestore = async () => {
      if (currentUser) {
        const cartDocRef = doc(db, 'carts', currentUser.uid);
        const cartDoc = await getDoc(cartDocRef);
        if (cartDoc.exists()) {
          // Here you might want to merge local and firestore carts
          // For simplicity, we'll just overwrite the local cart
          setCartItems(cartDoc.data().items || []);
        }
      }
    };
    loadCartFromFirestore();
  }, [currentUser]);

  const addToCart = async (product: Product, quantity: number = 1, _options?: AddToCartOptions) => {
    // Check if this is a new product being added (not just quantity increase)
    const isNewProduct = !cartItems.find(item => item.id === product.id && item.type !== 'gift');
    
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id && item.type !== 'gift');
      
      if (existingItem) {
        // If item already exists, increase quantity
        // Preserve wasPrice when updating quantity
        return prevItems.map(item =>
          item.id === product.id && item.type !== 'gift' 
            ? { ...item, quantity: item.quantity + quantity, wasPrice: (product as any).wasPrice || item.wasPrice } 
            : item
        );
      } else {
        // Otherwise, add new item with quantity of 1
        // Explicitly include wasPrice if it exists
        const newItem: CartItem = {
          ...product,
          quantity,
          type: 'product',
          wasPrice: (product as any).wasPrice
        };
        return [...prevItems, newItem];
      }
    });

    // Trigger gift selection when a new product is added (only once per session)
    // Make sure product.type is undefined or not 'gift' (products don't have type by default)
    const isGiftProduct = product.type === 'gift';
    const shouldTrigger = !hasTriggeredGifts.current && !isGiftProduct && isNewProduct;
    
    if (shouldTrigger) {
      hasTriggeredGifts.current = true;
      triggerGiftSelection();
    }

    setSideCartOpen(true);
  };

  const triggerGiftSelection = async () => {
    try {
      // Check if gift system is enabled
      const settings = await giftService.getSettings();
      
      if (!settings.enabled) {
        return;
      }

      // Select random gifts
      const result = await giftService.selectRandomGifts();
      
      if (result.gifts && result.gifts.length > 0) {
        setSelectedGifts(result.gifts);
        
        // Auto-accept if enabled
        if (result.autoAccept) {
          addGiftsToCart(result.gifts);
        } else if (result.shouldShowPopup) {
          // Show popup if enabled
          setShowGiftPopup(true);
        } else {
          // If popup is disabled but auto-accept is also disabled, still add gifts
          addGiftsToCart(result.gifts);
        }
      } else {
        // Reset the trigger so user can try again after adding gifts
        hasTriggeredGifts.current = false;
      }
    } catch (error) {
      console.error('❌ Error triggering gift selection:', error);
      // Reset the trigger on error so user can try again
      hasTriggeredGifts.current = false;
    }
  };

  const addGiftsToCart = (gifts: GiftItem[]) => {
    const giftCartItems: CartItem[] = gifts.map(gift => ({
      id: `gift-${gift.id}`,
      name: gift.name,
      price: 0, // Gifts are free
      brand: 'StringPak Gift',
      gauge: '',
      imageURL: gift.imageURL,
      stock: 999,
      description: gift.description || `Free gift: ${gift.name}`,
      category: 'Gift',
      type: 'gift' as const,
      quantity: 1
    }));

    setCartItems(prevItems => {
      // Remove any existing gifts first to avoid duplicates
      const nonGiftItems = prevItems.filter(item => item.type !== 'gift');
      return [...nonGiftItems, ...giftCartItems];
    });

    setShowGiftPopup(false);
  };

  const removeFromCart = (productId: string) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const increaseQuantity = (productId: string) => {
    setCartItems(prevItems => {
      return prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    });
  };

  const decreaseQuantity = (productId: string) => {
    setCartItems(prevItems => {
      return prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity: item.quantity > 1 ? item.quantity - 1 : 1 }
          : item
      );
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    const num = Math.max(1, Math.floor(Number(quantity)) || 1);
    setCartItems(prevItems => {
      return prevItems.map(item =>
        item.id === productId ? { ...item, quantity: num } : item
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
    hasTriggeredGifts.current = false; // Reset gift trigger when cart is cleared
    setShowGiftPopup(false);
    setSelectedGifts([]);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    updateQuantity,
    clearCart,
    isSideCartOpen,
    setSideCartOpen,
    // Gift functionality
    showGiftPopup,
    setShowGiftPopup,
    selectedGifts,
    addGiftsToCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
